import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Flex,
  IconButton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { MagnifyingGlassMinus, MagnifyingGlassPlus, X, ArrowsClockwise } from 'phosphor-react';

interface DocumentoPreviewDrawerProps {
  file: File | null;
  isOpen: boolean;
  onClose: () => void;
}

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

const DRAWER_HEIGHT_DEFAULT_VH = 55;
const DRAWER_HEIGHT_MIN_VH = 25;
const DRAWER_HEIGHT_MAX_VH = 85;

// Drawer placement=top sem overlay — operador continua interagindo com o
// form abaixo (preenchimento/confirmacao dos campos). Conforme decisao UX
// 2026-05-19: visualizar o documento no topo da tela e os campos do socio
// imediatamente abaixo, sem necessidade de fechar o preview pra conferir.
//
// Zoom controlado pra imagem (img + transform scale). PDF usa iframe (zoom
// nativo do browser PDF viewer — Chrome/Firefox tem controles proprios).
export const DocumentoPreviewDrawer = ({ file, isOpen, onClose }: DocumentoPreviewDrawerProps) => {
  const [zoom, setZoom] = useState(1);
  // Altura do drawer em vh — operador ajusta via drag da borda inferior.
  // Persistir entre sessoes nao foi escopo do MVP; default 55vh sempre.
  const [drawerHeightVh, setDrawerHeightVh] = useState(DRAWER_HEIGHT_DEFAULT_VH);
  const dragStartRef = useRef<{ y: number; initialVh: number } | null>(null);

  const objectUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  // Libera o object URL quando o arquivo muda ou o componente desmonta —
  // se nao revogar, fica vazando memoria no browser.
  useEffect(
    () => () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    },
    [objectUrl],
  );

  // Reseta zoom toda vez que abre/troca de arquivo pra evitar "ficar com
  // 300% num doc anterior".
  useEffect(() => {
    if (isOpen) setZoom(1);
  }, [isOpen, file]);

  const isImage = file?.type.startsWith('image/') ?? false;
  const isPdf = file?.type === 'application/pdf';

  const onZoomIn = () => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  const onZoomOut = () => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  const onZoomReset = () => setZoom(1);

  // Resize do drawer via drag da borda inferior. Listeners no document
  // pra capturar o arrasto mesmo se o cursor sair da handle. Durante o
  // drag, desabilita pointer-events em TODOS os iframes da pagina —
  // sem isso, iframe de preview de PDF captura o mousemove pro seu
  // contexto isolado e document parent nao recebe, fazendo o drag "se
  // soltar" quando cursor passa sobre o iframe. Restaurado em onResizeEnd.
  const setIframesPointerEvents = (value: string) => {
    document.querySelectorAll<HTMLIFrameElement>('iframe').forEach(f => {
      // eslint-disable-next-line no-param-reassign
      f.style.pointerEvents = value;
    });
  };

  const onResizeMove = useCallback((e: MouseEvent) => {
    if (!dragStartRef.current) return;
    const deltaPx = e.clientY - dragStartRef.current.y;
    const viewportHeight = window.innerHeight;
    const deltaVh = (deltaPx / viewportHeight) * 100;
    const novoVh = Math.max(
      DRAWER_HEIGHT_MIN_VH,
      Math.min(DRAWER_HEIGHT_MAX_VH, dragStartRef.current.initialVh + deltaVh),
    );
    setDrawerHeightVh(novoVh);
  }, []);

  const onResizeEnd = useCallback(() => {
    dragStartRef.current = null;
    document.removeEventListener('mousemove', onResizeMove);
    document.removeEventListener('mouseup', onResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    setIframesPointerEvents('');
  }, [onResizeMove]);

  const onResizeStart = (e: React.MouseEvent) => {
    dragStartRef.current = { y: e.clientY, initialVh: drawerHeightVh };
    document.addEventListener('mousemove', onResizeMove);
    document.addEventListener('mouseup', onResizeEnd);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    setIframesPointerEvents('none');
    e.preventDefault();
  };

  // Cleanup safety net — se componente desmontar durante drag, sem isso
  // listeners ficam pendurados E iframes ficam sem pointer-events.
  useEffect(
    () => () => {
      document.removeEventListener('mousemove', onResizeMove);
      document.removeEventListener('mouseup', onResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setIframesPointerEvents('');
    },
    [onResizeMove, onResizeEnd],
  );

  return (
    <Drawer
      placement="top"
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      // Permite scroll da pagina (form abaixo) enquanto drawer aberto.
      // Chakra bloqueia scroll do body por default — comportamento certo
      // pra modais focais, errado pro nosso drawer-painel (operador
      // confere campos abaixo enquanto ve o doc no topo). Scroll dentro
      // do drawer continua funcionando pelo overflow=auto do DrawerBody.
      blockScrollOnMount={false}
    >
      {/* SEM <DrawerOverlay /> — operador deve continuar interagindo com o
          form abaixo do drawer. */}
      {/* Altura controlada por state — operador ajusta via drag da borda
          inferior (handle). Default 55vh; range 25vh-85vh. h e maxH
          explicitos pra Chakra nao auto-crescer com conteudo grande. */}
      <DrawerContent h={`${drawerHeightVh}vh`} maxH={`${drawerHeightVh}vh`}>
        <DrawerHeader borderBottomWidth="1px" px={4} py={2}>
          <Flex justify="space-between" align="center">
            <Text fontSize="md" fontWeight="bold" noOfLines={1}>
              Documento — {file?.name ?? '(sem arquivo)'}
            </Text>
            <IconButton
              aria-label="Fechar preview"
              icon={<X size={20} />}
              onClick={onClose}
              variant="ghost"
              size="sm"
            />
          </Flex>
        </DrawerHeader>

        <DrawerBody overflow="auto" p={2} bg="gray.50">
          {!file && (
            <Flex justify="center" align="center" h="100%">
              <Spinner />
            </Flex>
          )}
          {file && isImage && objectUrl && (
            <Box
              overflow="auto"
              h="100%"
              w="100%"
              display="flex"
              alignItems="flex-start"
              justifyContent="center"
            >
              <img
                src={objectUrl}
                alt={file.name}
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top center',
                  // Limita ao tamanho do container — sem isso, imagem
                  // portrait grande (CNH ~4000x5000) escapa do drawer e
                  // ocupa a tela toda apesar do maxH no DrawerContent.
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transition: 'transform 0.15s ease',
                }}
              />
            </Box>
          )}
          {file && isPdf && objectUrl && (
            <Box h="100%" w="100%">
              {/* iframe usa o PDF viewer nativo do browser — controles de
                  zoom proprios do Chrome/Firefox/Edge. Pra imagens
                  controlamos via state local. */}
              <iframe
                src={objectUrl}
                title={file.name}
                width="100%"
                height="100%"
                style={{ border: 'none', minHeight: '50vh' }}
              />
            </Box>
          )}
          {file && !isImage && !isPdf && (
            <Flex justify="center" align="center" h="100%">
              <Text color="gray.500" fontSize="sm">
                Preview indisponivel para este tipo de arquivo: {file.type}
              </Text>
            </Flex>
          )}
        </DrawerBody>

        {file && isImage && (
          <DrawerFooter borderTopWidth="1px" py={2} px={4}>
            <Flex gap={2} align="center" w="100%" justify="center">
              <IconButton
                aria-label="Diminuir zoom"
                icon={<MagnifyingGlassMinus size={18} />}
                onClick={onZoomOut}
                size="sm"
                isDisabled={zoom <= ZOOM_MIN}
              />
              <Text fontSize="sm" minW="60px" textAlign="center">
                {Math.round(zoom * 100)}%
              </Text>
              <IconButton
                aria-label="Aumentar zoom"
                icon={<MagnifyingGlassPlus size={18} />}
                onClick={onZoomIn}
                size="sm"
                isDisabled={zoom >= ZOOM_MAX}
              />
              <Button
                leftIcon={<ArrowsClockwise size={16} />}
                onClick={onZoomReset}
                size="sm"
                variant="ghost"
                ml={2}
                isDisabled={zoom === 1}
              >
                Reset
              </Button>
            </Flex>
          </DrawerFooter>
        )}

        {/* Handle de resize — operador arrasta verticalmente pra ajustar
            altura do drawer. Indicador visual minimalista (4px) que vira
            azul no hover. Cursor row-resize indica affordance. */}
        <Box
          onMouseDown={onResizeStart}
          h="6px"
          w="100%"
          cursor="row-resize"
          bg="gray.200"
          _hover={{ bg: 'blue.300' }}
          transition="background-color 0.15s"
          role="separator"
          aria-label="Redimensionar preview do documento"
          aria-orientation="horizontal"
          position="relative"
        >
          {/* Indicador visual no centro (3 traços) */}
          <Flex
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            gap="3px"
            pointerEvents="none"
          >
            <Box w="20px" h="2px" bg="gray.500" borderRadius="full" />
          </Flex>
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default DocumentoPreviewDrawer;
